
import React from 'react';
import {
   Users, Building2, TrendingUp, Activity,
   ShieldCheck, Globe, Database, Zap,
   ArrowUpRight, AlertCircle, CreditCard,
   Server, Lock, CheckCircle2, Search, Filter, Sliders, Trash2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, PieChart, Pie } from 'recharts';

const SYSTEM_STATS = [
   { label: 'Totaal Bedrijven', value: '142', icon: <Building2 size={16} />, trend: '+4', color: 'text-red-', bg: 'bg-red-' },
   { label: 'Actieve Gebruikers', value: '892', icon: <Users size={16} />, trend: '+12', color: 'text-emerald-600', bg: 'bg-emerald-50' },
   { label: 'Systeem Credits', value: '2.4M', icon: <CreditCard size={16} />, trend: '+150k', color: 'text-amber-600', bg: 'bg-amber-50' },
   { label: 'Totaal Hires', value: '4,120', icon: <CheckCircle2 size={16} />, trend: '+42', color: 'text-red-600', bg: 'bg-red-50' },
];

const REVENUE_DATA = [
   { month: 'Jan', revenue: 45000 },
   { month: 'Feb', revenue: 52000 },
   { month: 'Mar', revenue: 48000 },
   { month: 'Apr', revenue: 61000 },
   { month: 'May', revenue: 55000 },
   { month: 'Jun', revenue: 67000 },
];

const CLIENT_TYPE_DATA = [
   { name: 'Directe Klanten', value: 65, fill: '#e51b23' },
   { name: 'Agencies', value: 35, fill: '#0F172A' },
];

const AdminDashboard: React.FC = () => {
   const [stats, setStats] = React.useState(SYSTEM_STATS);
   const [isLoading, setIsLoading] = React.useState(true);
   const [activeTab, setActiveTab] = React.useState<'overview' | 'companies' | 'users'>('overview');
   const [clientsList, setClientsList] = React.useState<any[]>([]);
   const [profilesList, setProfilesList] = React.useState<any[]>([]);

   React.useEffect(() => {
      let mounted = true;
      const fetchStats = async () => {
         try {
            // Timeout safety
            const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Stats timeout')), 10000));

            const statsPromise = (async () => {
               // In a real app, these would be separate optimized RPC calls
               // For now, we simulate or fetch simple counts if possible, or just keep mock data
               // To prove fetching works, let's try to count clients
               const clients = await import('../services/supabaseService').then(m => m.supabase_get_clients());
               const vacancies = await import('../services/supabaseService').then(m => m.supabase_get_vacancies());
               const profiles = await import('../services/supabaseService').then(m => m.supabase_get_recruiters());

               return {
                  clientsCount: clients?.length || 142,
                  vacanciesCount: vacancies?.length || 892,
                  clients: clients || [],
                  profiles: profiles || []
               };
            })();

            const res = await Promise.race([statsPromise, timeout]) as any;

            if (mounted && res) {
               setStats(prev => [
                  { ...prev[0], value: res.clientsCount.toString() },
                  { ...prev[1], value: res.vacanciesCount.toString() }, // Using vacancies as "Active Users" proxy for now
                  prev[2],
                  prev[3]
               ]);
               setClientsList(res.clients);
               setProfilesList(res.profiles);
            }
         } catch (e) {
            console.warn("Admin stats load error", e);
         } finally {
            if (mounted) setIsLoading(false);
         }
      };

      fetchStats();
      return () => { mounted = false; };
   }, []);

   return (
      <div className="space-y-6 pb-20">
         {/* System Status Header */}
         <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200">
            <div>
               <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase tracking-widest rounded-sm">Master Admin</span>
                  <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">{isLoading ? 'Syncing...' : 'Health: Optimal'}</span>
               </div>
               <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                  <Sliders className="text-[#e51b23] shrink-0" size={24} />
                  System Controller
               </h1>
            </div>
            <div className="flex gap-2">
               <div className="px-3 py-1 bg-white border border-slate-200 rounded-sm flex items-center gap-2 shadow-sm">
                  <Server size={12} className="text-emerald-500" />
                  <div className="text-left">
                     <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest leading-none">CPU</p>
                     <p className="text-[10px] font-bold text-slate-700">12%</p>
                  </div>
               </div>
               <div className="px-3 py-1 bg-white border border-slate-200 rounded-sm flex items-center gap-2 shadow-sm">
                  <Database size={12} className="text-red-500" />
                  <div className="text-left">
                     <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest leading-none">DB</p>
                     <p className="text-[10px] font-bold text-slate-700">24ms</p>
                  </div>
               </div>
            </div>
         </div>

         {/* Global Stats Grid */}
         <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
               <div key={i} className="bg-white p-4 rounded-md border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                     <div className={`p-1.5 rounded-sm ${stat.bg} ${stat.color}`}>{stat.icon}</div>
                     <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-sm border border-emerald-100">{stat.trend}</span>
                  </div>
                  <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest truncate">{stat.label}</p>
                  <h3 className="text-lg font-bold text-slate-900 mt-0.5 tracking-tight">{isLoading ? '...' : stat.value}</h3>
               </div>
            ))}
         </div>

         {/* Admin Tabs */}
         <div className="flex border-b border-slate-200 mt-8 mb-4 overflow-x-auto custom-scrollbar">
            {[
               { id: 'overview', label: 'Overzicht' },
               { id: 'companies', label: 'Bedrijven Beheer' },
               { id: 'users', label: 'Gebruikers Beheer' }
            ].map(tab => (
               <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'overview' | 'companies' | 'users')}
                  className={`px-6 py-3 border-b-2 text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${activeTab === tab.id
                     ? 'border-red-600 text-red-600'
                     : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                     }`}
               >
                  {tab.label}
               </button>
            ))}
         </div>

         {/* Overview Tab (Original Content) */}
         {activeTab === 'overview' && (
            <>
               <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Global Revenue Chart */}
                  <div className="lg:col-span-8 bg-white p-5 rounded-md border border-slate-200 shadow-sm">
                     <div className="flex items-center justify-between mb-6">
                        <div>
                           <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Global Credit Consumption</h3>
                           <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tight mt-0.5">Revenue Stream</p>
                        </div>
                        <div className="flex gap-2">
                           <button className="p-1.5 bg-slate-50 text-slate-400 rounded-sm hover:bg-slate-100 border border-slate-200"><Filter size={14} /></button>
                        </div>
                     </div>
                     {/* FIXED HEIGHT CONTAINER */}
                     <div className="h-[300px] w-full min-h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                           <AreaChart data={REVENUE_DATA}>
                              <defs>
                                 <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#e51b23" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#e51b23" stopOpacity={0} />
                                 </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 600, fill: '#64748b' }} />
                              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 600, fill: '#64748b' }} />
                              <Tooltip
                                 contentStyle={{ borderRadius: '4px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontSize: '11px', fontWeight: 600, padding: '8px' }}
                              />
                              <Area type="monotone" dataKey="revenue" stroke="#e51b23" strokeWidth={2} fillOpacity={1} fill="url(#colorRev)" />
                           </AreaChart>
                        </ResponsiveContainer>
                     </div>
                  </div>

                  {/* Client Distribution */}
                  <div className="lg:col-span-4 bg-white p-5 rounded-md border border-slate-200 shadow-sm flex flex-col">
                     <div className="mb-4">
                        <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest">Account Verdeling</h3>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tight mt-0.5">Ratio</p>
                     </div>
                     {/* FIXED HEIGHT CONTAINER */}
                     <div className="h-[300px] w-full min-h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                           <PieChart>
                              <Pie
                                 data={CLIENT_TYPE_DATA}
                                 cx="50%"
                                 cy="50%"
                                 innerRadius={60}
                                 outerRadius={80}
                                 paddingAngle={5}
                                 dataKey="value"
                              >
                                 {CLIENT_TYPE_DATA.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                 ))}
                              </Pie>
                              <Tooltip />
                           </PieChart>
                        </ResponsiveContainer>
                     </div>
                     <div className="space-y-2 mt-2">
                        {CLIENT_TYPE_DATA.map((item, idx) => (
                           <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm border border-slate-100">
                              <div className="flex items-center gap-2">
                                 <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }}></div>
                                 <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">{item.name}</span>
                              </div>
                              <span className="text-[10px] font-bold text-slate-900">{item.value}%</span>
                           </div>
                        ))}
                     </div>
                  </div>
               </div>

               {/* System Alerts & Quick Actions */}
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                  <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
                     <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                        <h3 className="text-[10px] font-bold text-slate-900 uppercase tracking-widest">Security Monitor</h3>
                        <span className="flex items-center gap-1.5 text-[9px] font-bold text-emerald-600 uppercase tracking-widest">
                           <ShieldCheck size={12} /> Active
                        </span>
                     </div>
                     <div className="divide-y divide-slate-100">
                        {[
                           { msg: 'Nieuwe client registratie: TechFuture BV', time: '14m ago', status: 'Notice', icon: <Building2 size={12} /> },
                           { msg: 'Ongebruikelijke inlogpoging (IP: 192.1.2.3)', time: '1h ago', status: 'Warning', icon: <Lock size={12} /> },
                        ].map((alert, i) => (
                           <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                              <div className="flex items-center gap-3">
                                 <div className={`p-1.5 rounded-sm ${alert.status === 'Warning' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                                    {alert.icon}
                                 </div>
                                 <div>
                                    <p className="text-[10px] font-bold text-slate-800 uppercase tracking-tight">{alert.msg}</p>
                                    <p className="text-[9px] text-slate-400 font-semibold uppercase tracking-tighter mt-0.5">{alert.time}</p>
                                 </div>
                              </div>
                              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-widest border ${alert.status === 'Warning' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                 'bg-red- text-red- border-red-'
                                 }`}>{alert.status}</span>
                           </div>
                        ))}
                     </div>
                  </div>

                  <div className="bg-[#0F172A] rounded-md p-6 text-white relative overflow-hidden flex flex-col justify-center">
                     <div className="relative z-10 space-y-4">
                        <h3 className="text-lg font-bold tracking-tight uppercase leading-none">System <br /><span className="text-red-600">Config</span></h3>
                        <p className="text-slate-400 text-[10px] font-medium max-w-xs leading-relaxed italic">
                           Pas systeem-brede parameters aan.
                        </p>
                        <div className="grid grid-cols-2 gap-2 pt-2">
                           <button className="bg-white text-slate-900 py-2 rounded-sm font-bold text-[9px] uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all shadow-sm">API Beheer</button>
                           <button className="bg-slate-800 text-white py-2 rounded-sm font-bold text-[9px] uppercase tracking-widest hover:bg-slate-700 transition-all">Logs</button>
                        </div>
                     </div>
                     <div className="absolute right-[-20px] bottom-[-20px] text-white/5 rotate-12">
                        <Zap size={120} fill="currentColor" />
                     </div>
                  </div>
               </div>
            </>
         )}

         {/* Companies Management Tab */}
         {activeTab === 'companies' && (
            <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
               <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                     <Building2 size={16} className="text-slate-400" /> Bedrijven Actief
                  </h3>
                  <span className="text-[10px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-sm">{clientsList.length} BEDRIJVEN</span>
               </div>
               {clientsList.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm font-medium">Geen bedrijven gevonden of lokaal niet gesynct.</div>
               ) : (
                  <div className="overflow-x-auto">
                     <table className="w-full text-left border-collapse">
                        <thead>
                           <tr className="bg-slate-50/50">
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Bedrijfsnaam</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Type / Email</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Toegevoegd Op</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 w-[100px] text-right">Acties</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                           {clientsList.map((client) => (
                              <tr key={client.id} className="hover:bg-slate-50/50 transition-colors group">
                                 <td className="px-5 py-3">
                                    <div className="font-bold text-slate-900 text-sm">{client.name || 'Onbekend Bedrijf'}</div>
                                    <div className="text-[10px] font-mono text-slate-400">{client.id}</div>
                                 </td>
                                 <td className="px-5 py-3">
                                    <div className="text-xs font-medium text-slate-700">{client.type || 'Regulier'}</div>
                                    <div className="text-[10px] text-slate-400 truncate max-w-[150px]">{client.email || 'Geen email'}</div>
                                 </td>
                                 <td className="px-5 py-3 text-xs text-slate-600 font-medium">
                                    {client.created_at ? new Date(client.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}
                                 </td>
                                 <td className="px-5 py-3 text-right">
                                    <button
                                       onClick={async () => {
                                          if (window.confirm('Weet je ZEKER dat je dit bedrijf wilt verwijderen?\nDit verwijdert ook alle gekoppelde vacatures, kandidaten en profielen. Dit kan NIET ongedaan worden gemaakt.')) {
                                             try {
                                                await import('../services/supabaseService').then(m => m.supabase_admin_delete_company(client.id));
                                                setClientsList(prev => prev.filter(c => c.id !== client.id));
                                                alert('Bedrijf succesvol verwijderd!');
                                             } catch (e: any) {
                                                alert('Verwijderen mislukt: ' + (e.message || 'Onbekende fout'));
                                             }
                                          }
                                       }}
                                       className="p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                       title="Verwijder Bedrijf"
                                    >
                                       <Trash2 size={16} />
                                    </button>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}
            </div>
         )}

         {/* Users Management Tab */}
         {activeTab === 'users' && (
            <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
               <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                     <Users size={16} className="text-slate-400" /> Gebruikers Actief
                  </h3>
                  <span className="text-[10px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-sm">{profilesList.length} GEBRUIKERS</span>
               </div>
               {profilesList.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 text-sm font-medium">Geen profielen gevonden of lokaal niet gesynct.</div>
               ) : (
                  <div className="overflow-x-auto">
                     <table className="w-full text-left border-collapse">
                        <thead>
                           <tr className="bg-slate-50/50">
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Naam / Email</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Rol / Bedrijf ID</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">Status</th>
                              <th className="px-5 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 w-[100px] text-right">Acties</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                           {profilesList.map((profile) => (
                              <tr key={profile.id} className="hover:bg-slate-50/50 transition-colors group">
                                 <td className="px-5 py-3">
                                    <div className="font-bold text-slate-900 text-sm">{profile.name || 'Onbekende Gebruiker'}</div>
                                    <div className="text-[10px] text-slate-500">{profile.email}</div>
                                 </td>
                                 <td className="px-5 py-3">
                                    <div className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm uppercase inline-block bg-slate-100 text-slate-700">{profile.role || 'ONBEKEND'}</div>
                                    <div className="text-[9px] font-mono text-slate-400 mt-1 truncate max-w-[120px]">{profile.client_id || 'Geen gekoppeld bedrijf'}</div>
                                 </td>
                                 <td className="px-5 py-3 text-xs text-slate-600 font-medium">
                                    <div className="flex items-center gap-1.5">
                                       <div className={`w-1.5 h-1.5 rounded-full ${profile.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                                       <span>{profile.status || 'Active'}</span>
                                    </div>
                                 </td>
                                 <td className="px-5 py-3 text-right">
                                    <button
                                       onClick={async () => {
                                          if (window.confirm(`Weet je ZEKER dat je gebruiker "${profile.name}" wilt verwijderen? Dit verbreekt de toegang tot het systeem.`)) {
                                             try {
                                                await import('../services/supabaseService').then(m => m.supabase_admin_delete_profile(profile.id));
                                                setProfilesList(prev => prev.filter(p => p.id !== profile.id));
                                                alert('Gebruiker profiel succesvol verwijderd (Opmerking: Auth account blijft in Supabase bestaan en moet mogelijk via het platform handmatig gedeletet worden om het emailadres volledig te bevrijden).');
                                             } catch (e: any) {
                                                alert('Verwijderen mislukt: ' + (e.message || 'Onbekende fout'));
                                             }
                                          }
                                       }}
                                       className="p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                       title="Verwijder Gebruiker"
                                    >
                                       <Trash2 size={16} />
                                    </button>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default AdminDashboard;
